import { EmptyState } from "@/components/molecules/empty-state";
import { ErrorState } from "@/components/molecules/error-state";
import { PageSkeleton } from "@/components/molecules/page-skeleton";
import { useMyClients } from "@/features/clients/hooks/use-clients";
import { useTranslation } from "@/features/utils/i18n";
import { useTitle } from "@/features/utils/use-title";
import { BuildingsIcon } from "@phosphor-icons/react";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { DeleteCompanySection } from "./components/delete-company";
import { CallRecordingSection } from "./components/call-recording-section";
import { CompanySettingsGeneral } from "./components/general-section";
import { RestartOnboardingSection } from "./components/restart-onboarding";
import { PageLayout } from "../layout";

export function CompanySettingsPage() {
  const { t } = useTranslation("companySettings");
  const { company: clientId } = useParams<{ company: string }>();
  const {
    data: clients = [],
    isLoading,
    isError,
    refetch,
  } = useMyClients();

  useTitle(t("companySettings.title"));

  // Find current client
  const currentClient = useMemo(() => {
    return clients.find((c) => c.client.id === clientId);
  }, [clients, clientId]);

  if (isLoading) {
    return <PageSkeleton width="medium" blocks={["h-[28rem]", "h-40"]} />;
  }

  if (!currentClient) {
    return (
      <PageLayout width="medium" title={t("companySettings.title")}>
        {isError ? (
          <ErrorState onRetry={refetch} />
        ) : (
          <EmptyState
            icon={BuildingsIcon}
            title={t("companySettings.notFound")}
          />
        )}
      </PageLayout>
    );
  }

  return (
    <PageLayout
      width="medium"
      title={t("companySettings.title")}
      description={t("companySettings.description")}
    >
      <CompanySettingsGeneral />
      <CallRecordingSection />
      <RestartOnboardingSection />
      <DeleteCompanySection />
    </PageLayout>
  );
}
